import type { ReactNode } from "react";
import { Card } from "./Card";

/**
 * The shell's whole-page states — the ones that replace the panel rather than appearing
 * inside it.
 *
 * **Generic copy, never a vendor error string or code** — the login app's rule (issue #57
 * acceptance), and it applies here for the same reason plus one of its own: this page is
 * reachable by anyone who types the host, and its errors are read by a family who came to fix
 * something and cannot.
 */

/** A branded spinner shown while the SDK works out whether this browser holds a session. */
export function Loading({ label = "Loading…" }: { label?: string }) {
  return (
    <div className="state" role="status" aria-live="polite">
      <div className="spinner" aria-hidden="true" />
      <p className="state__body">{label}</p>
    </div>
  );
}

/** A generic error panel — no vendor detail, and always with something to do next. */
export function ErrorState({ title, body }: { title: string; body: string }) {
  return (
    <div className="state" role="alert">
      <p className="state__title">{title}</p>
      <p className="state__body">{body}</p>
    </div>
  );
}

/**
 * Shown when the build shipped without a Stytch public token or without one of the hosts it
 * has to call (src/stytch.ts, src/config.ts) — a misconfiguration, surfaced calmly rather than
 * as a blank page. Deliberately says nothing about why.
 */
export function ConfigError() {
  return (
    <Card>
      <ErrorState title="This page isn't ready yet" body="Please try again in a little while." />
    </Card>
  );
}

/**
 * Nobody is signed in on this browser.
 *
 * **No chrome at all** (#293): no family switcher, no nav, no account chip — nothing to
 * suggest there is something behind it. Just the lockup, one sentence, and the ways in.
 *
 * **Two ways in, not one, and that is the point of this card.** It used to offer *Sign in* and
 * nothing else, on the assumption that a first-time visitor arrives at the marketing apex and
 * only a returning member ever types this host. That assumption is not ours to make: somebody
 * who has heard of Boone and guesses at a URL lands here, and a lone *Sign in* button tells
 * them they need an account they do not have. Nothing on the page said how to get one, and the
 * route that would have — the server's own joining sentence — is unreachable from here, which
 * is the whole difficulty (see below).
 *
 * **This card cannot ask the server anything.** There is no session, so there is no
 * `GET /app/me/families` and therefore no `nextStep` to render. That is what makes it different
 * from {@link NoFamily}, where #158's "the words are the server's" rule both applies and
 * suffices. Here the offer is composed in this bundle or it is not made at all, and
 * {@link startFamilyUrl} is what keeps it honest: it returns `null` where the posture flag is
 * off, so this never offers a door the login app would answer "not open yet".
 *
 * **There is no sign-in here and there is not meant to be**: ADR 0010 keeps one central login
 * page so this app reuses it rather than becoming a second Stytch integration. So this hands
 * them over, with a `return_to` that brings them back to the page they were trying to open —
 * validated on arrival there against `*.iamboone.app`, never trusted from this bundle. The
 * create hand-off carries no `return_to`, for the reason {@link startFamilyUrl} records.
 *
 * It is also what a session that has ended mid-visit lands on, and #305 measured that rather
 * than leaving it to feel. The session ends on a wall clock an hour after sign-in — nothing the
 * panel does extends it, because the SDK asks for a longer session only when built with
 * `keepSessionAlive`, which `STYTCH_CLIENT_OPTIONS` deliberately does not set — and the browser
 * arrives here within three minutes of that, or at once on the way back to a tab that was
 * asleep. **A graceful expiry is the whole of what is needed**, and the `return_to` above is
 * what makes it graceful: they come back to the page they were reading.
 *
 * **It deliberately does not say "your session ended".** That would be a second state, and the
 * thing it must not fire on is the account chip's own sign-out, which arrives here by exactly
 * the same route — `user` going null — and would be told its deliberate act had happened to it.
 * Worth revisiting when a screen holds unsaved work (#302): that is when losing the page starts
 * costing something, and it is the one thing the measurement leaves open.
 */
export function SignedOut({
  signInUrl,
  startFamilyUrl,
}: {
  signInUrl: string | null;
  startFamilyUrl: string | null;
}) {
  return (
    <Card>
      {/*
        The sentence tracks the offer below it. Promising "or start one" where the posture flag
        is off would describe a button that is not on the page — a smaller version of exactly
        the failure this card was fixing.
      */}
      <p className="subhead subhead--centred">
        {startFamilyUrl === null
          ? "Sign in to see what Boone keeps for your family."
          : "Sign in to see what Boone keeps for your family — or start one, if you're new here."}
      </p>
      {signInUrl === null ? (
        <ErrorState
          title="Sign-in isn't available from here"
          body="Please try again in a little while."
        />
      ) : (
        <a className="cta cta--block" href={signInUrl}>
          Sign in
        </a>
      )}
      {startFamilyUrl === null ? null : (
        <div className="signedout__alt">
          <p className="subhead subhead--centred signedout__altlabel">New to Boone?</p>
          <a className="cta cta--block cta--quiet" href={startFamilyUrl}>
            Start your family
          </a>
        </div>
      )}
    </Card>
  );
}

/**
 * A signed-in person with no family they can open — either they belong to none, or every one
 * they belong to is being deleted.
 *
 * **The chip stays and the rest of the chrome goes** (#293): they are signed in, and this is
 * exactly where a wrong account gets noticed — the shared-laptop case, and the one MCP cannot
 * help with, because its `unknown_identity` refusal can only *warn* that this might be a
 * different sign-in where a panel can show whose it is. The switcher and the nav do not stay,
 * because there is nothing to switch between and nowhere to go.
 *
 * **The words are the server's** (`GET /app/me/families` → `nextStep`), and that is the whole
 * design of this state. A family-less person can already reach two pieces of copy — Boone's
 * introduction and every family tool's refusal — and #158 is what happened when those two
 * drifted. A third version written in a browser is the same bug with a wider audience, so this
 * component renders a string and composes none.
 *
 * `onRetry` is here because the instruction ends with going somewhere else and coming back:
 * the panel cannot know when they have, and a reload is a worse answer than a button.
 *
 * **The heading is deliberately posture-blind.** The two postures this serves are "you have not
 * joined a family" and "the family you were in is being erased", and any heading that fitted
 * the first — *nothing here yet*, *not set up* — is false about the second, which is the tense
 * mistake #228's copy review already caught once. So the heading identifies the page and the
 * server's sentence says what is true.
 */
export function NoFamily({ nextStep, onRetry }: { nextStep: string; onRetry: () => void }) {
  return (
    <div className="empty">
      <h1 className="heading">Your control panel</h1>
      <p className="subhead">{linkify(nextStep)}</p>
      <button className="cta" type="button" onClick={onRetry}>
        Check again
      </button>
    </div>
  );
}

/**
 * Render the `https://` URLs inside a server string as links, leaving every other character
 * exactly as it arrived.
 *
 * The wireframes give this state a *Start a family* button, and the affordance here is instead
 * the server's own URL made clickable. This is not copy the panel composes: React escapes every
 * part, so nothing here can inject markup, and a string with no URL in it comes back unchanged.
 *
 * **The argument that kept the button out was "a fourth build input", and that input now
 * exists** — `VITE_FAMILY_SELF_CREATE_ENABLED`, added for {@link SignedOut}, which had no
 * server sentence to fall back on. So the cost this paragraph priced is already paid, and what
 * is left is the narrower question of whether a button beats an inline link for somebody who is
 * *signed in* and family-less. It probably does, and it is deliberately not part of that change:
 * unlike the signed-out card, this state has a working route today, and the panel can now do
 * better than a redirect to the login host — `POST /app/me/families` serves a caller with no
 * households, so the honest version is the `Households` form rendered in place rather than a
 * link out. That is a screen, not a link, and wants its own change.
 *
 * **#360 revisited this and left it, which is worth recording because that issue is the one
 * that gave the panel a create.** `POST /app/me/families` serves somebody with no household
 * perfectly well, and *Your households* is where a member starts a second — but this state is
 * the one place the flag argument above still bites, and the acceptance criterion is explicitly
 * *"starts their first from the panel, **or is sent somewhere that works** — in the server's own
 * words"*. A button here would need the flag as a build input to avoid offering a door that
 * 403s, which is the fourth input this component was written to avoid. The link the server
 * already hands them works. What would change this is the server saying so in a field rather
 * than in a sentence.
 */
function linkify(text: string): ReactNode {
  return text.split(/(https:\/\/[^\s,)]+)/g).map((part, index) =>
    part.startsWith("https://") ? (
      // The parts of one immutable split: never reordered, inserted into, or removed.
      // biome-ignore lint/suspicious/noArrayIndexKey: the array is a split of a constant.
      <a key={index} href={part}>
        {part}
      </a>
    ) : (
      part
    ),
  );
}
